import {Button, Card, CardBody, CardHeader, Divider, Tabs, Tab} from "@nextui-org/react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import { getFetcher } from "../../../../services/api";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import CustomDataGrid from "../../../../components/elements/CustomDataGrid";
import React, { useMemo, useState } from "react";
import {format} from "date-fns";

export default function DistribusiPencarian() {
    const currentDate = new Date();
    const formattedDate = format(currentDate, 'dd-MM-yyyy');

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState(formattedDate);
    const [endDate, setEndDate] = useState(formattedDate);
    const [keyword, setKeyword] = useState('');

    const swrOptions = {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
        revalidateOnMount: false,
        dedupingInterval: 60000,
        suspense: false,
        fallbackData: null,
    };

    /*
     * Alamat pencarian memakai `page=${halaman}`, bukan `page=${page}1`.
     *
     * Bentuk lama menyambung teks: halaman 0 kebetulan menghasilkan "01" = 1
     * sehingga tampak benar, tetapi halaman 2 meminta page 11 dan halaman 3
     * meminta page 21. Jumlah baris pun dulu dipaku 10, jadi memilih 50 baris
     * per halaman tetap mengambil 10.
     */
    const halaman = page + 1; // DataGrid menghitung dari 0, API dari 1

    const { data: reportedB3, mutate: mutateReportedb3 } = useSWR(
        `/api/dashboard/pelaporan/distribusi/pencarian/bahanb3?page=${halaman}&limit=${pageSize}&startDate=${startDate}&endDate=${endDate}&keyword=${keyword}`,
        getFetcher,
        swrOptions
    );

    const { data: companies, mutate: mutateCompanies } = useSWR(
        `/api/dashboard/pelaporan/distribusi/pencarian/perusahaan?page=${halaman}&limit=${pageSize}&startDate=${startDate}&endDate=${endDate}&keyword=${keyword}`,
        getFetcher,
        swrOptions
    );

    const columnsB3 = useMemo(() => [
        {
            field: 'namaBahanKimia',
            headerName: 'Nama Bahan Kimia',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.namaBahanKimia || '-'}</span>,
        },
        {
            field: 'casNumber',
            headerName: 'Cas Number',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.casNumber || '-'}</span>,
        },
        {
            field: 'namaDagang',
            headerName: 'Nama Dagang',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.namaDagang || '-'}</span>,
        },
        {
            field: 'tipeBahan',
            headerName: 'Tipe Bahan',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.tipeBahan || '-'}</span>,
        },
         {
            field: 'sum',
            headerName: 'Total Distribusi',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.sum || '-'}</span>,
        },
    ], []);

     const columnsPerusahaan = useMemo(() => [
        {
            field: 'name',
            headerName: 'Nama Perusahaan',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.name || '-'}</span>,
        },
        {
            field: 'penanggungJawab',
            headerName: 'Penanggung Jawab',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.penanggungJawab || '-'}</span>,
        },
        {
            field: 'alamatKantor',
            headerName: 'Alamat Kantor',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.alamatKantor || '-'}</span>,
        },
        {
            field: 'nomorInduk',
            headerName: 'Nomor Induk',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.nomorInduk || '-'}</span>,
        },
        {
            field: 'kodeDBKlh',
            headerName: 'Kode DBK1H',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.kodeDBKlh || '-'}</span>,
        },
        {
            field: 'total',
            headerName: 'Total Distribusi',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.total || '-'}</span>,
        },  
    ], []);

    const handleRefresh = () => {
        mutateReportedb3();
        mutateCompanies();
    };

    const handleGetData = () => {
        mutateReportedb3();
        mutateCompanies();
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    };

    const handleChange = (event) => {
        setKeyword(event.target.value);
    };

    return (
        <RootAdmin>
            <Card className="w-full" radius="sm">
                <CardHeader className="flex justify-between items-center">
                    <p className="text-md">Dashboard Pencarian Pelaporan Distribusi B3</p>
                    <div className="flex gap-2">
                        <Button size="sm" color="primary" onPress={handleGetData}>
                            Get Data
                        </Button>
                        <Button size="sm" color="secondary" startContent={<ArrowPathIcon className="size-4" />} onPress={handleRefresh}>
                            Refresh
                        </Button>
                    </div>
                </CardHeader>
                <Divider />
                <CardBody className="w-full h-[550px] p-5">
                    <Filter
                        startDate={startDate}
                        handleStartDate={handleStartDateChange}
                        endDate={endDate}
                        handleEndDate={handleEndDateChange}
                        keyword={keyword}
                        handleKeyword={handleChange}
                    />
                    <Tabs aria-label="Pelaporan Pencarian B3" defaultValue="reportedB3">
                        <Tab title="Pelaporan Berdasarkan Jenis B3" value="reportedB3">
                            <CustomDataGrid
                                data={reportedB3?.responseData?.data || []}
                                rowCount={reportedB3?.responseData?.total || 0}
                                columns={columnsB3}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                page={page}
                                setPage={setPage}
                            />
                        </Tab>
                        <Tab title="Pelaporan Berdasarkan Perusahaan" value="companies">
                            <CustomDataGrid
                                data={companies?.responseData?.data || []}
                                rowCount={companies?.responseData?.total || 0}
                                columns={columnsPerusahaan}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                page={page}
                                setPage={setPage}
                            />
                        </Tab>
                    </Tabs>
                </CardBody>
            </Card>
        </RootAdmin>
    );
}

function Filter({ startDate, handleStartDate, endDate, handleEndDate, keyword, handleKeyword }) {
    return(
        <div className="flex items-center gap-3 mb-10">
            <div className="flex flex-row w-1/8 border-b-2 border-bottom-gray">
                <div className="flex items-center mr-5">
                    <span>Start Date:</span>
                </div>
                <input
                    type="date"
                    id="date-input"
                    value={startDate}
                    onChange={handleStartDate}
                />
            </div>
            <div className="flex flex-row w-1/8 border-b-2 border-bottom-gray">
                <div className="flex items-center mr-5">
                    <span>End Date:</span>
                </div>
                <input
                    type="date"
                    id="date-input"
                    value={endDate}
                    onChange={handleEndDate}
                />
            </div>
            <div className="flex flex-row w-1/8 border-b-2 border-bottom-gray">
                <div className="flex items-center mr-5">
                    <span>Keyword:</span>
                </div>
                <input
                    type="input"
                    id="input"
                    value={keyword}
                    onChange={handleKeyword}
                />
            </div>
        </div>
    )
}
